import { ClipboardList, FileText, HeartPulse, Target } from "lucide-react";

import type { CompanyProfileData } from "@/types/businessData";
import { hasProductAccessWithFallback } from "@/utils/productAccess";

import type { Nudge, NudgeContext } from "./types";

// Profile sections tracked in `companyProfile.completedSteps` and filled in
// under /settings/profile. Decoupled from the signup flow's steps — signup
// only covers the company profile; the rest is product-specific setup.
export const PROFILE_SECTION_STEPS = [
  "company-profile",
  "icp",
  "sales-info",
  "compensation",
  "hiring-plan",
  "leads-config",
] as const;

const completeCompanyProfileNudge: Nudge = {
  id: "complete-company-profile",
  priority: 40,
  evaluate(ctx: NudgeContext) {
    const { businessProfile } = ctx;
    if (!businessProfile) return null;

    const profile = businessProfile.companyProfile as
      | CompanyProfileData
      | undefined;
    const completedSteps = profile?.completedSteps ?? [];
    const hasLeads = businessProfile.interestedProducts?.includes("leads");

    const applicableSteps = PROFILE_SECTION_STEPS.filter(
      (s) => s !== "leads-config" || hasLeads,
    );
    const remaining = applicableSteps.filter(
      (s) => !completedSteps.includes(s),
    );
    if (remaining.length === 0) return null;

    const done = applicableSteps.length - remaining.length;

    return {
      icon: ClipboardList,
      severity: "info",
      accent: "info",
      title: "Slutför er affärsprofil",
      description: `${done} av ${applicableSteps.length} steg klara`,
      progress: { done, total: applicableSteps.length },
      cta: { label: "Fyll i affärsprofil", to: "/settings/profile" },
    };
  },
};

const pendingSubmittedLeadsNudge: Nudge = {
  id: "pending-submitted-leads",
  priority: 70,
  evaluate(ctx: NudgeContext) {
    const {
      businessProfile,
      pendingSubmittedLeadsCount,
      submittedLeadsLoading,
    } = ctx;
    if (!businessProfile) return null;

    const leadsEnabled = hasProductAccessWithFallback(
      businessProfile,
      "leads",
      Date.now(),
    );
    if (!leadsEnabled) return null;
    if (submittedLeadsLoading) return null;
    if (
      pendingSubmittedLeadsCount == null ||
      pendingSubmittedLeadsCount === 0
    ) {
      return null;
    }

    const n = pendingSubmittedLeadsCount;
    const description =
      n === 1
        ? "1 mottagen lead väntar på beslut."
        : `${n} mottagna leads väntar på beslut.`;

    return {
      icon: FileText,
      severity: "warning",
      accent: "warning",
      title: "Du har leads att granska",
      description,
      cta: { label: "Granska leads", to: "/leads" },
    };
  },
};

const pulseSetupRequiredNudge: Nudge = {
  id: "pulse-setup-required",
  priority: 76,
  evaluate(ctx: NudgeContext) {
    if (!ctx.pulseSetupNudge?.show) return null;

    return {
      icon: HeartPulse,
      severity: "info",
      accent: "info",
      title: "Konfigurera Pulse check-in",
      description:
        "Välj hur ofta ert team ska göra check-in och gör er första bedömning på fyra signaler.",
      cta: { label: "Konfigurera Pulse", to: "/coach/performance/pulse" },
    };
  },
};

const kpiSetupRequiredNudge: Nudge = {
  id: "kpi-setup-required",
  // Below Pulse setup: a team should exist and be doing check-ins before KPIs
  // are worth defining.
  priority: 72,
  evaluate(ctx: NudgeContext) {
    if (!ctx.kpiSetupNudge?.show) return null;

    return {
      icon: Target,
      severity: "info",
      accent: "info",
      title: "Välj era nyckeltal",
      description:
        "Bestäm upp till sex nyckeltal per team och fyll i utfallet varje period — det driver analyser, insikter och medarbetarsammanfattningar.",
      cta: { label: "Välj nyckeltal", to: "/coach/teams" },
    };
  },
};

const pulseCheckInDueNudge: Nudge = {
  id: "pulse-check-in-due",
  priority: 75,
  evaluate(ctx: NudgeContext) {
    const pulse = ctx.pulseNudge;
    if (!pulse?.show) return null;

    const { scoredCount, totalMembers, periodLabel, periodStart, periodEnd } =
      pulse;
    const description =
      scoredCount > 0
        ? `Du har bedömt ${scoredCount} av ${totalMembers} för perioden ${periodLabel} (avslutad).`
        : `Perioden ${periodLabel} är avslutad — bedöm hur teamet presterade på fyra signaler.`;

    const params = new URLSearchParams({
      periodStart,
      periodEnd,
    });

    return {
      icon: HeartPulse,
      severity: "warning",
      accent: "warning",
      title: "Dags för Pulse check-in",
      description,
      progress: { done: scoredCount, total: totalMembers },
      cta: {
        label: "Starta Pulse",
        to: `/coach/performance/pulse?${params.toString()}`,
      },
      starterLabel: `Summera perioden ${periodLabel}`,
      suggestedPrompt: `Sammanfatta hur teamet presterade under perioden ${periodLabel} utifrån Pulse check-in, och vad jag bör prioritera härnäst.`,
    };
  },
};

// Pulse data signals (momentum/wellbeing/period-summary) used to live here as
// client-side nudges. They are now produced server-side by the insight
// generators (`apps/business-api/.../insights/generators/pulse.ts`) and merged
// into the dashboard via `useDashboardInsights`. Only app-state nudges — those
// about what the user still needs to set up or action — remain client-side.

export const defaultNudges: Nudge[] = [
  completeCompanyProfileNudge,
  pendingSubmittedLeadsNudge,
  pulseSetupRequiredNudge,
  kpiSetupRequiredNudge,
  pulseCheckInDueNudge,
];
